import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable bottom-of-page lead capture banner for non-login pages.
 * Submits to POST /api/leads (public, no auth), fires admin email notification.
 */
export default function LeadCaptureCTA({ source_page, headline, subline, interests }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', role_title: '', interest: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, { ...form, source_page });
      setDone(true);
      toast.success("Thanks — we'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-5xl mx-auto mt-12 mb-8">
        <div className="bg-gradient-to-br from-emerald-500 to-[#004ac6] rounded-2xl p-10 text-center text-white shadow-xl"
             data-testid="lead-capture-success">
          <CheckCircle className="w-14 h-14 mx-auto mb-4 text-white" />
          <h2 className="text-2xl font-extrabold tracking-tight mb-2">Message received</h2>
          <p className="text-sm text-white/80 max-w-lg mx-auto">
            Our team will reach out within one business day with a tailored walkthrough of Sovereign IQ for your use case.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-5xl mx-auto mt-12 mb-8" data-testid="lead-capture-cta">
      <div className="bg-gradient-to-br from-[#0b1c30] via-[#1a365d] to-[#004ac6] rounded-2xl p-8 lg:p-10 text-white shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 70% 20%, rgba(124,58,237,0.5) 0%, transparent 50%)` }} />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-[#a78bfa]" /> Talk to our team
            </span>
            <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tighter mb-3">
              {headline || 'See your first country blueprint in under 90 minutes'}
            </h2>
            <p className="text-sm text-white/70 leading-relaxed mb-4">
              {subline || 'Schedule a 20-minute walkthrough with our team, or start exploring Sovereign IQ directly. We partner with investment funds, DFIs, governments, and multinationals.'}
            </p>
            <ul className="space-y-1.5 text-xs text-white/80">
              <li className="flex items-start gap-2"><span className="text-[#a78bfa]">✓</span> 31-section investment-grade country blueprints on demand</li>
              <li className="flex items-start gap-2"><span className="text-[#a78bfa]">✓</span> Live World Bank + IMF data, 1,000+ sources</li>
              <li className="flex items-start gap-2"><span className="text-[#a78bfa]">✓</span> PDF, Gamma deck, audio brief, and Sora 2 cinematic video</li>
            </ul>
          </div>

          <form onSubmit={onSubmit} className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/10 space-y-3">
            <div className="grid grid-cols-2 gap-2.5">
              <input type="text" placeholder="Full name" value={form.name}
                     onChange={e => onChange('name', e.target.value)}
                     className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-xs placeholder-white/50 text-white outline-none focus:border-[#a78bfa]"
                     data-testid="lead-name" />
              <input type="email" required placeholder="Work email *" value={form.email}
                     onChange={e => onChange('email', e.target.value)}
                     className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-xs placeholder-white/50 text-white outline-none focus:border-[#a78bfa]"
                     data-testid="lead-email" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <input type="text" placeholder="Company" value={form.company}
                     onChange={e => onChange('company', e.target.value)}
                     className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-xs placeholder-white/50 text-white outline-none focus:border-[#a78bfa]"
                     data-testid="lead-company" />
              <input type="text" placeholder="Role (e.g. Partner, MD)" value={form.role_title}
                     onChange={e => onChange('role_title', e.target.value)}
                     className="bg-white/10 border border-white/20 rounded-md px-3 py-2 text-xs placeholder-white/50 text-white outline-none focus:border-[#a78bfa]"
                     data-testid="lead-role" />
            </div>
            <select value={form.interest} onChange={e => onChange('interest', e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-xs text-white outline-none focus:border-[#a78bfa]"
                    data-testid="lead-interest">
              <option value="" className="text-black">Primary interest…</option>
              {(interests || [
                'Private equity / investment fund',
                'Development finance (DFI, IFC, AfDB)',
                'Multinational corporate strategy',
                'Consulting firm / advisory',
                'Government / sovereign client',
                'Other',
              ]).map(i => <option key={i} value={i} className="text-black">{i}</option>)}
            </select>
            <textarea rows="2" placeholder="Brief message (optional)" value={form.message}
                      onChange={e => onChange('message', e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-md px-3 py-2 text-xs placeholder-white/50 text-white outline-none focus:border-[#a78bfa] resize-none"
                      data-testid="lead-message" />
            <button type="submit" disabled={submitting}
                    data-testid="lead-submit"
                    className="w-full flex items-center justify-center gap-2 bg-white text-[#004ac6] font-bold text-xs py-2.5 rounded-md hover:bg-[#e5eeff] transition-all disabled:opacity-60">
              {submitting ? <><div className="w-3.5 h-3.5 border-2 border-[#004ac6]/30 border-t-[#004ac6] rounded-full animate-spin" /> Sending…</> :
                <><Send className="w-3.5 h-3.5" /> Request walkthrough</>}
            </button>
            <p className="text-[9px] text-white/40 text-center">We'll reply within 1 business day. No spam.</p>
          </form>
        </div>
      </div>
    </section>
  );
}
